import { Box, Button, Stack, TablePagination, Typography } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { ResType } from "types";
import {
  getViderAuditRequests,
  updateViderAuditRequest,
} from "api/services/organizations";
import Loader from "components/Loader";
import Table from "components/Table";
import { useState } from "react";
import SearchContainer from "components/SearchContainer";
import { useNavigate } from "react-router-dom";
import { useConfirm } from "context/ConfirmDialog";
import { snack } from "components/toast";

const ViderAuditRequests = () => {
  const [search, setSearch] = useState<string>("");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const confirm = useConfirm();

  const { data, isLoading }: ResType = useQuery("viderauditrequests", getViderAuditRequests);

  const { mutate, isLoading: isUpdating } = useMutation(updateViderAuditRequest, {
    onSuccess: () => {
      snack.success("Audit request updated");
      queryClient.invalidateQueries("viderauditrequests");
    },
    onError: (err: any) => {
      snack.error(err?.response?.data?.message || "Failed to update audit request");
    },
  });

  const handleStatusChange = (
    row: any,
    responseStatus: "COMPLETED" | "REJECTED"
  ) => {
    confirm({
      msg:
        responseStatus === "COMPLETED"
          ? "Mark this request as COMPLETED? This will enable Audit for the organization."
          : "Reject this audit request?",
      action: () => mutate({ id: row?.id, responseStatus }),
    });
  };
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [page, setPage] = useState(0);

  const startIndex = page * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;

  const handleSearch = (value: string) => {
    setSearch(value);
    setPage(0);
  };

  if (isLoading || isUpdating) return <Loader />

  const term = search.trim().toLowerCase();
  const filteredData = term
    ? (data?.data || []).filter((item: any) => {
        const org = item?.user?.organization;
        return [
          org?.mobileNumber,
          org?.legalName,
          org?.email,
          org?.id,
          item?.responseStatus,
        ]
          .filter((v) => v !== undefined && v !== null)
          .some((v) => String(v).toLowerCase().includes(term));
      })
    : data?.data || [];

  const paginatedData = filteredData.slice(startIndex, endIndex);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);

    if (![25, 50, 75, 100].includes(newRowsPerPage)) {
      setPage(0);
    }
  };
  return (
    <Box m={2}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mt={2}
      >
        <Typography variant="h5" m={1}>Vider Audit Requests</Typography>

        <SearchContainer
          minWidth="400px"
          placeHolder="Search by Mobile / Name / Email / Org Id"
          onChange={handleSearch}
          debounced
          value={search}
        />

      </Box>
      <Box mt={2}>
        <Table
          columns={[
            {
              title: "Organization Id",
              key: "user.organization.id",
            },
            {
              title: "Organization Name",
              key: "user.organization.legalName",
            },
            {
              title: "Mobile Number",
              key: "user.organization.mobileNumber",
            },
            {
              title: "Email",
              key: "user.organization.email",
            },
            {
              title: "responseStatus",
              key: "responseStatus",
              render: (item) => (
                <Typography fontWeight={500}>
                  {item?.responseStatus}
                </Typography>
              ),
            },
            {
              title: "Actions",
              key: "id",
              render: (item) => (
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    variant="contained"
                    color="secondary"
                    disabled={item?.responseStatus !== "PENDING" || isUpdating}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleStatusChange(item, "COMPLETED");
                    }}
                  >
                    Complete
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    disabled={item?.responseStatus !== "PENDING" || isUpdating}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleStatusChange(item, "REJECTED");
                    }}
                  >
                    Reject
                  </Button>
                </Stack>
              ),
            },
          ]}
          loading={isLoading}
          data={paginatedData || []}
          onRowClick={(item) => {
            navigate(`/organizations/${item?.user?.organization?.id}`);
          }}
        />
        <TablePagination
          rowsPerPageOptions={[25, 50, 75, 100]}
          component="div"
          count={filteredData.length || 0}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{ position: 'sticky', bottom: 0, backgroundColor: 'white', zIndex: 1 }}

        />
      </Box>
    </Box>
  );
};

export default ViderAuditRequests;
